import React, { useRef, useState } from 'react';
import get from 'lodash/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components/macro';
import { useSelector } from 'src/client/redux';
import { BrandedPillButton, TEXT_SHADOW_LARGE, TEXT_SHADOW_SMALL } from './styles';
import { TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import urls, { constructUrl } from 'src/shared/urls';
import { ApiClient } from 'src/client/helpers';
import { faTimesCircle } from '@fortawesome/pro-light-svg-icons';

type Props = {
  setShowEmailSection: (show: boolean) => void;
};

export const FreeGalleryEmail = (props: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isEU = !useSelector((state) => state.auth.location.isEU);
  const [showConfirmSection, setShowConfirmSection] = useState(false);
  const [showUnknownError, setShowUnknownError] = useState(false);

  const [email, setEmail] = useState('');
  const [optIn, setOptIn] = useState(false);

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (inputRef.current) {
      inputRef.current.setCustomValidity('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowUnknownError(false);

    const api = new ApiClient();
    const data = { email, optIn };
    try {
      const { body } = await api.post(constructUrl(urls.post.signup.freeGalleriesMovies), { data });
      if (body.success) {
        return setShowConfirmSection(true);
      }

      throw new Error('Unexpected response from server');
    } catch (error: unknown) {
      const serverError = get(error, 'response.body.error', 'UNKNOWN_ERROR');

      if (serverError === 'INVALID_EMAIL') {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.setCustomValidity('Please enter a valid email address.');
        }
        return;
      }

      setShowUnknownError(true);
    }
  };

  return (
    <Wrapper>
      <EmailForm hidden={showConfirmSection} isEU={isEU} onSubmit={handleSubmit}>
        <span>Credit card not required. No obligation</span>
        <BigText>Want to see more?</BigText>
        <span>
          <b>Sign Up</b> Now to View 4 Complete Galleries for FREE
        </span>
        <StyledInput
          autoFocus
          name="email"
          onChange={handleEmailInputChange}
          placeholder="Your e-mail address"
          ref={inputRef}
          type="email"
          value={email}
        />
        {showUnknownError && <ErrorText>An unknown error occurred. Please try again later.</ErrorText>}
        <SignupButton as="button" type="submit">
          Start Your Free Trial
        </SignupButton>
        {isEU && (
          <OptInLabel>
            <input
              type="checkbox"
              name="optin"
              id="optin"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
            />
            <InfoLine>
              <span>Would you like to get free stuff?</span> Click Yes to join us to get occasional information about
              our products, services, events, news and offers.
            </InfoLine>
          </OptInLabel>
        )}
      </EmailForm>

      <ConfirmSection show={showConfirmSection}>
        <span>
          <b>Your 4 FREE Galleries</b> are now <b>Unlocked</b>
        </span>
        <InfoLine>
          Please go to your email <span>{email}</span>, to access the content.
        </InfoLine>
        <span>Please note to check your spam folder if you do not receive the email from us.</span>
      </ConfirmSection>
      <CloseButton onClick={() => props.setShowEmailSection(false)}>
        <FontAwesomeIcon icon={faTimesCircle} />
      </CloseButton>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  // above the slider
  z-index: 20;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  line-height: 1.2;
  font-size: 12px;
  ${TEXT_SHADOW_SMALL}

  b {
    font-weight: 600;
  }
`;

const EmailForm = styled.form<{ hidden: boolean; isEU?: boolean }>`
  display: ${(props) => (props.hidden ? 'none' : 'flex')};
  flex-direction: column;
  align-items: center;
  justify-content: center;

  // more text to show for the eu optin box, so we need smaller gaps
  gap: ${(props) => (props.isEU ? '10px' : '20px')};
`;

const ConfirmSection = styled.section<{ show: boolean }>`
  display: ${(props) => (props.show ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
  padding: 0 20px;
`;

const CloseButton = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  padding: 10px;
  font-size: 28px;
`;

const BigText = styled.span`
  font-size: 32px;
  font-weight: 800;
  ${TEXT_SHADOW_LARGE}
`;

const StyledInput = styled.input`
  width: 320px;
  padding: 10px;
  text-align: center;
  font-size: 16px;
  margin-bottom: 0;
  color: ${(props) => props.theme.primary4};

  &:focus {
    outline: none;
  }

  &&:invalid {
    border-color: red;
  }
`;

export const SignupButton = styled(BrandedPillButton)`
  --height: 48px;
  font-size: 18px;
  width: 100%;
  max-width: 320px;
  border: 0;

  @media ${TABLET_PORTRAIT} {
    --height: 36px;
    font-size: 14px;
  }
`;

const OptInLabel = styled.label`
  user-select: none;
  max-width: 320px;
  display: flex;
  margin-bottom: 0;
  justify-content: center;
  align-items: flex-start;
  font-weight: normal;

  input {
    margin: 15px 15px 15px 0;
  }
`;

const ErrorText = styled.span`
  color: #ff0033;
  font-size: 12px;
  ${TEXT_SHADOW_SMALL}
`;

const InfoLine = styled.span`
  span {
    color: var(--brand-color);
  }
`;
